import {
  canRateProgramObject,
  isSuperAdmin,
  canManageCommunityProfile,
  isProgramAdminProfile,
  canLeaveCommunityProfile,
  isProgramAuthorProfile,
  hasMembershipInProgramProfile,
  hasMembershipInCommunityProfile,
  isOrganizationAdminProfile,
  canSeeFuturePosts,
} from 'permissions';
import { isAfter, parseISO } from 'date-fns';
import { getUserFromStore } from 'features/auth';

const isProgram = ({ program }) => Boolean(program?.id);

const isProgramManager = ({ program, viewer }) => {
  if (!isProgram({ program })) {
    return false;
  }

  return (
    isProgramAdminProfile({ programId: program.id, viewer }) ||
    isProgramAuthorProfile({ programId: program.id, viewer })
  );
};

export function can(record, type, action) {
  const viewer = getUserFromStore()?.railsUserData ?? null;

  const recordActions = {
    ContentObject: {
      post: (community) => {
        return (
          canManageCommunityProfile({ community, viewer }) ||
          (community.settings ? community.settings.allow_comments : false)
        );
      },
      viewPollChoicePercentage: (contentObject) => {
        return (
          Boolean(viewer) &&
          (contentObject.post_in_communities?.some((community) => canManageCommunityProfile({ community, viewer })) ||
            contentObject.author.id === viewer.id)
        );
      },
    },
    Program: {
      contentCreator: (program) =>
        Boolean(viewer) && (isSuperAdmin({ viewer }) || isProgramManager({ program, viewer })),
      editProgramContent: (program) => recordActions.Program.contentCreator(program) && program.status !== 'finished',
      createProgramContent: (program) => recordActions.Program.editProgramContent(program),
      deleteProgramContent: (program) => recordActions.Program.contentCreator(program),
      editProgram: (program) =>
        program.status !== 'finished' &&
        program.end_time &&
        isAfter(parseISO(program.end_time), new Date()) &&
        recordActions.Program.contentCreator(program),
      editAdmins: (program) => !program.id || isProgramAuthorProfile({ programId: program.id, viewer }),
      seeFuturePosts: (program) => canSeeFuturePosts({ viewer: getUserFromStore(), program }),
      viewProgram: (program) =>
        isProgram({ program }) &&
        ((hasMembershipInProgramProfile({ programId: program.id, viewer }) && program.status === 'published') ||
          isProgramManager({ program, viewer }) ||
          isSuperAdmin({ viewer })),
      rateProgramObject: (item) => canRateProgramObject({ viewer, contentObject: item }),
      downloadReport: (program) => recordActions.Program.contentCreator(program),
      inviteMembers: (program) =>
        program?.status === 'published' &&
        (recordActions.Program.seeFuturePosts(program) ||
          (program.organization && isOrganizationAdminProfile({ viewer, organizationId: program.organization.id }))),
      isMemberOfProgram: (program) =>
        isProgram({ program }) &&
        (hasMembershipInProgramProfile({ programId: program.id, viewer }) || isProgramManager({ program, viewer })),
      joinedAsUser: (program) =>
        isProgram({ program }) && hasMembershipInProgramProfile({ programId: program.id, viewer }),
      hasUserSentJoinRequest: (program) => program?.privacy === 'private' && program?.requested_to_join,
      canUnsubscribe: (program) =>
        recordActions.Program.joinedAsUser(program) &&
        program.status === 'published' &&
        !program.mandatory &&
        program.settings.allow_unsubscribe,
    },
    Activity: {
      submitData: (activity) => !activity?.is_withings && !activity?.is_dexcom,
    },
    Community: {
      joined: (community) => hasMembershipInCommunityProfile({ communityId: community.id, viewer }),
      leaveCommunity: (community) => canLeaveCommunityProfile({ viewer, community }),
    },
  };

  return recordActions[type][action](record);
}
